import {IPresentationMeta} from '../incase-domain/presentation';
import {PLAYER_HOST, PLAYER_PRES_ID_QUERY, PLAYER_ROOM_ID_QUERY, PRESENTER_CODE_QUERY} from '../incase-domain/online';
import {ProtectionType} from '../incase-domain/presentationProtection';
import {isRedirectParam} from './redirect';

export function isPresentationRunnable(protectionType: ProtectionType[] | undefined): boolean {
    const runnableProtections = [
        ProtectionType.GUEST,
        ProtectionType.PASSWORD,
        ProtectionType.REGISTERED_USERS,
        ProtectionType.SELECTED_USERS,
        ProtectionType.AAI_EDU,
        ProtectionType.EMEDIKUS,
        ProtectionType.ZOOM,
    ];

    if (!protectionType) {
        return false;
    }

    return runnableProtections.some((type) => protectionType.includes(type));
}

interface ICreatePlayPresentationUrlParams {
    presentation: IPresentationMeta;
    isCustom?: boolean;
    roomId?: string;
    slideId?: string;
    presenterCode?: string;
}

export const createPlayPresentationUrl = ({
    presentation,
    isCustom,
    roomId,
    slideId,
    presenterCode,
}: ICreatePlayPresentationUrlParams): string => {
    let url = '';
    if (roomId) {
        url = `${PLAYER_HOST}?${PLAYER_ROOM_ID_QUERY}=${roomId}`;
    } else {
        url = `${PLAYER_HOST}?${PLAYER_PRES_ID_QUERY}=${presentation._id}`;
    }

    if (presenterCode) {
        url += `&${PRESENTER_CODE_QUERY}=${presenterCode}`;
    }

    if (slideId) {
        url += `&slideId=${slideId}`;
    }

    // case when admin is opening presentation that requires request params
    if (presentation.requestParams) {
        presentation.requestParams.forEach((param) => {
            // Redirect keys are optional and handled by the player
            if (isRedirectParam(param.key) && !(isCustom && param.value)) return;
            if (isCustom && param.required && !param.value) {
                throw new Error(`Missing required request param: ${param.key}`);
            } else if (isCustom && param.value) {
                url += `&${param.key}=${encodeURIComponent(param.value)}`;
            } else if (!isCustom) {
                url += `&${param.key}=ADMIN`;
            }
        });
        return url;
    }
    return url;
};

export const buildAdminRequestParams = (presentation: IPresentationMeta): {key: string; value: string}[] => {
    return (presentation.requestParams ?? [])
        .filter((param) => !isRedirectParam(param.key))
        .map((param) => ({key: param.key, value: 'ADMIN'}));
};

export const createShortPresentationUrl = (slug: string): string => {
    return `${PLAYER_HOST}/presentation/${slug}`;
};

export const createShortRoomUrl = (slug: string): string => {
    return `${PLAYER_HOST}/room/${slug}`;
};

export const openPlayerWindow = (buildUrl: () => Promise<string>, fallbackUrl?: string): void => {
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    buildUrl()
        .then((url) => {
            if (win) win.location.href = url;
        })
        .catch(() => {
            if (!win) return;
            if (fallbackUrl) win.location.href = fallbackUrl;
            else win.close();
        });
};
